/**
 * Shared types + helpers para industry valuation.
 *
 * Los tipos viven acá para que /pages/Industry.tsx y los sub-componentes
 * (<IndustryJobRow>, etc.) compartan la misma shape de ValuedJob. Antes
 * esto vivía dentro del page, lo que forzaba duplicación en cada componente
 * que tocaba jobs.
 */

export type JobStatus =
  | 'active'
  | 'paused'
  | 'ready'
  | 'delivered'
  | 'cancelled'
  | 'reverted'

export type Material = {
  type_id: number
  name: string
  quantity: number
  jita_buy: number | null
  line_cost: number
}

export type ValuedJob = {
  job_id: number
  activity_id: number
  activity_name: string
  blueprint_type_id: number
  blueprint_name: string
  product_type_id: number | null
  product_name: string | null
  runs: number
  status: JobStatus
  start_date: string
  end_date: string
  progress: number
  remaining_seconds: number
  product_jita_sell: number | null
  expected_total_value: number | null
  expected_value_per_hour: number | null
  materials: Material[] | null
  material_cost_per_run: number | null
  material_cost_total: number | null
  facility_cost_total: number | null
  margin_per_run: number | null
  margin_total: number | null
  margin_per_hour: number | null
  margin_pct: number | null
  materials_note: string | null
  // 'esi' cuando el backend usó ESI per-job materials (skills + structure +
  // rig bonuses aplicados). 'fuzzwork' cuando usó el SDE sin bonos. Null
  // para research jobs.
  materials_source: 'esi' | 'fuzzwork' | null
}

/** Jobs que ya terminaron (no se actualiza el progress bar). */
export function isDone(status: JobStatus): boolean {
  return status === 'delivered' || status === 'cancelled' || status === 'reverted'
}

/** Formatea segundos como "5d 3h" / "12m 30s" / "ahora". */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return 'ahora'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/** Color classes para el status badge. Centralizadas para que cambios
 *  de palette toquen un solo lugar. */
export const JOB_STATUS_BADGE: Record<JobStatus, string> = {
  active: 'bg-green-500/10 text-green-400 border-green-500/30',
  paused: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  ready: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  delivered: 'bg-eve-muted/10 text-eve-muted border-eve-border',
  cancelled: 'bg-red-500/10 text-red-400 border-red-500/30',
  reverted: 'bg-red-500/10 text-red-400 border-red-500/30',
}
